<template>
    <div :id="'slide-card-' + slide._id" class="slide-card-content" @click="openTreatment">
        <div class="slide-card-header" :class="vendorClass">
            <div class="card-credentials">
                <i class="fa fa-circle mr-2" :style="'color: ' + patient.color" />

                <span class="main-entity-id">
                    <patient-id :patient="patient" :vendor="instrumentType" />
                    <span class="v-divider">|</span>
                </span>

                <span
                    v-b-tooltip.hover
                    class="treatment-id"
                    :title="`${$utils.getTreatmentEsIdLabelByVendor(
                        instrumentType
                    )} ${treatmentEsIdByVendor}`"
                >
                    {{ treatmentEsIdByVendor }}
                    <span class="v-divider">|</span>
                </span>

                <span
                    v-if="isVitro && showSlideID"
                    v-b-tooltip.hover
                    class="main-entity-id"
                    :title="`${$translate('slide')} ${$uiUtils.getVitrolifeSlideIdWithDescription(
                        slide
                    )}`"
                >
                    {{ $uiUtils.getVitrolifeSlideIdWithDescription(slide) }}
                </span>

                <span
                    v-if="isGeri || isMiri"
                    v-b-tooltip.hover
                    class="slide-position"
                    :title="slidePositionTitle"
                >
                    <span class="v-divider">|</span>
                    {{ slidePositionPrefix }} #{{ slide.slidePosition }}
                </span>
            </div>

            <age-info :treatment="slide.treatment" :slide="slide" />
        </div>

        <div class="slide-card-body">
            <div class="px-3 h-100 display-flex column justify-center">
                <span
                    v-b-tooltip.hover
                    class="text-brand w-auto mb-1"
                    :title="$translate('time_since_fertilization')"
                >
                    <span :id="`time-since-fertilization-${slide._id}`" class="text-bold">
                        {{ $translate('d').toUpperCase() }}
                        {{
                            $utils.getDaysSinceFertilization(slide.hoursSinceFertilization)
                        }}&nbsp;&nbsp;|&nbsp; {{ slide.hoursSinceFertilization }}H
                    </span>
                </span>

                <span class="text-light-1 mb-1">
                    {{
                        isGeri || isMiri
                            ? $translate('treatment_start_date')
                            : $translate('fertilization')
                    }}:
                </span>

                <span class="text-light-1 mb-1">
                    {{ $uiUtils.formatDate(slide.fertilizationDate) }}
                </span>

                <span class="text-light-2">
                    {{ $translate('wells') }}: {{ slide.wellsCount }}
                </span>
            </div>

            <template v-if="!isFdaFriendly">
                <div v-if="!slide.isEarlyStage" class="w-50 pr-3 pt-3 py-2 h-100 mr-2">
                    <common-card-score-metric
                        v-for="(bestWell, idx) in bestWells"
                        :key="idx"
                        :well="bestWell"
                        class="mb-2"
                    />
                </div>

                <div v-else class="slide-inference-inactive w-50 h-100">
                    <div class="py-4 pr-4 text-size-11 flex-column mt-3">
                        <div class="justify-between text-light-2 mb-1 pb-1 px-1 border-bottom">
                            <span>{{ $translate('tpna') }}</span>
                            <span>{{ slide.earlyStageData.wellsInPNa }}</span>
                        </div>

                        <div class="justify-between text-light-2 mb-1 pb-1 px-1 border-bottom">
                            <span>{{ $translate('tpnf') }}</span>
                            <span>{{ slide.earlyStageData.wellsInPNf }}</span>
                        </div>

                        <div class="justify-between text-light-2 mb-1 pb-1 px-1">
                            <span>{{ $translate('t2') }}</span>
                            <span>{{ slide.earlyStageData.wellsIn2C }}</span>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import AgeInfo from './common/AgeInfo'
import CommonCardScoreMetric from './common/CommonCardScoreMetric'
import { INSTRUMENT_TYPE } from '../../business/const'
import PatientId from './partials/PatientId'

export default {
    name: 'SlideCardContent',
    components: { PatientId, AgeInfo, CommonCardScoreMetric },
    props: {
        slide: {
            type: Object,
            required: true
        },
        index: {
            type: Number,
            required: true
        }
    },
    computed: {
        ...mapState({
            user: state => state.common.user,
            patients: state => state.timelapse.patients
        }),
        ...mapGetters({
            isFdaFriendly: 'common/isFdaFriendly'
        }),
        bestWells() {
            return this.slide.isEarlyStage ? [] : this.slide.bestWells
        },
        patient() {
            return this.patients.find(patient => patient._id === this.slide.patientId)
        },
        showSlideID() {
            const { tenants } = this.user
            const tenant = (tenants && tenants[0]) || {}
            return !(tenant.name && tenant.name === 'crgh2021')
        },
        instrumentType() {
            return (
                this.slide.instrumentType || (this.slide.instrument && this.slide.instrument.type)
            )
        },
        isGeri() {
            return this.instrumentType === INSTRUMENT_TYPE.GERI
        },
        isMiri() {
            return this.instrumentType === INSTRUMENT_TYPE.MIRI
        },
        isVitro() {
            return this.instrumentType === INSTRUMENT_TYPE.VITRO
        },
        vendorClass() {
            const { GERI, MIRI, VITRO } = INSTRUMENT_TYPE

            return {
                [GERI]: 'geri',
                [MIRI]: 'miri',
                [VITRO]: 'embryo-scope'
            }[this.instrumentType]
        },
        slidePositionTitle() {
            const { GERI, MIRI, VITRO } = INSTRUMENT_TYPE

            const slidePositionByVendor = {
                [GERI]: this.$translate('current_chamber'),
                [MIRI]: this.$translate('current_compartment'),
                [VITRO]: ''
            }

            return slidePositionByVendor[this.instrumentType]
        },
        slidePositionPrefix() {
            switch (this.instrumentType) {
                case INSTRUMENT_TYPE.GERI:
                    return this.$translate('chamber').toLowerCase()
                case INSTRUMENT_TYPE.MIRI:
                    return this.$translate('comp')
                default:
                    return null
            }
        },
        treatmentEsIdByVendor() {
            return this.$utils.getTreatmentEsIdByVendor(
                this.instrumentType,
                this.slide.esId,
                this.slide.instrument.esId,
                this.slide.treatmentEsId || this.slide.treatment.esId
            )
        }
    },
    methods: {
        openTreatment() {
            const { patientId, treatment, _id } = this.slide

            this.$store.dispatch('navigation/navigateToTreatment', {
                patientId,
                treatmentId: treatment._id,
                slideId: _id
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.slide-card-content {
    height: 100%;
    padding: 0.25rem 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    cursor: pointer;

    .slide-card-header {
        width: 100%;
        padding: 0.5rem 1rem;
        display: flex;
        justify-content: space-between;
        border-bottom: $border-narrow-dark;

        .v-divider {
            border: none;
            padding: 0 0.25rem;
        }

        .card-credentials {
            display: flex;
            align-items: center;
            margin-right: 0.25rem;

            .main-entity-id {
                max-width: 100px;

                &:not(.full-id) {
                    @include text-ellipsis();
                }
            }

            @media screen and #{$new-break3close} {
                .fa-circle {
                    font-size: 10px;
                }
            }
        }

        &.geri,
        &.miri {
            font-size: 0.85rem;

            .card-credentials {
                flex-wrap: wrap;
                align-items: center;
            }
            .slide-position {
                color: $gray4;
            }
            .treatment-id {
                max-width: 120px;
            }
        }
    }

    .slide-card-body {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    &:hover {
        background: $dark-2 !important;
        box-shadow: 0 0 1rem $shadow;
    }
}
</style>
